// Admin API Service for Configuration Management
// This service handles CRUD operations for city and indicator configurations

import API_BASE_URL from './apiConfig';

// Helper to get auth headers (for future API integration)
const getAuthHeaders = () => {
  const session = localStorage.getItem('authSession');
  if (session) {
    try {
      const sessionData = JSON.parse(session);
      return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionData.token || ''}` // For future API auth
      };
    } catch (e) {
      return { 'Content-Type': 'application/json' };
    }
  }
  return { 'Content-Type': 'application/json' };
};

// ============================================
// Configuration Management (LocalStorage for now, API-ready)
// ============================================

const CONFIG_STORAGE_KEY = 'dashboard_configurations';
const DEFAULT_CONFIG = {
  cityDataAvailable: {
    'Delhi': true,
    'Mumbai': false,
    'Palava': false,
    'Chennai': false,
    'Ahmedabad': false,
    'Pune': false,
    'Hyderabad': false,
    'Kolkata': false,
    'Bangalore': false
  },
  indicatorsWithData: {
    'Delhi': [
      'Power outages (duration and geographic scope)',
      'Power Outage',
      'Groundwater stress levels'
    ]
  },
  cityApiKeys: {
    'Delhi': 'DASHBOARD_DELHI_KEY',
    'Mumbai': 'DASHBOARD_MUMBAI_KEY',
    'Palava': 'DASHBOARD_PALAVA_KEY',
    'Chennai': 'DASHBOARD_CHENNAI_KEY',
    'Ahmedabad': 'DASHBOARD_AHMEDABAD_KEY',
    'Pune': 'DASHBOARD_PUNE_KEY',
    'Hyderabad': 'DASHBOARD_HYDERABAD_KEY',
    'Kolkata': 'DASHBOARD_KOLKATA_KEY',
    'Bangalore': 'DASHBOARD_BANGALORE_KEY'
  }
};

// Initialize configuration from localStorage or use defaults
const initializeConfig = () => {
  const stored = localStorage.getItem(CONFIG_STORAGE_KEY);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch (e) {
      console.error('Error parsing stored config:', e);
      return DEFAULT_CONFIG;
    }
  }
  return DEFAULT_CONFIG;
};

// Save configuration to localStorage
const saveConfig = (config) => {
  localStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify(config));
};

// ============================================
// City Configuration APIs
// ============================================

/**
 * Get all city configurations
 */
export const getCityConfigurations = async () => {
  try {
    // For now, use localStorage. Replace with API call when backend is ready:
    // const response = await fetch(`${API_BASE_URL}/api/admin/cities`, {
    //   headers: getAuthHeaders()
    // });
    // return await response.json();
    
    const config = initializeConfig();
    return {
      success: true,
      data: config.cityDataAvailable,
      apiKeys: config.cityApiKeys
    };
  } catch (error) {
    console.error('Error fetching city configurations:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update city availability
 */
export const updateCityAvailability = async (city, isAvailable) => {
  try {
    // For now, use localStorage. Replace with API call when backend is ready:
    // const response = await fetch(`${API_BASE_URL}/api/admin/cities/${city}`, {
    //   method: 'PUT',
    //   headers: getAuthHeaders(),
    //   body: JSON.stringify({ isAvailable })
    // });
    // return await response.json();
    
    const config = initializeConfig();
    config.cityDataAvailable[city] = isAvailable;
    saveConfig(config);
    
    return { success: true, data: config.cityDataAvailable };
  } catch (error) {
    console.error('Error updating city availability:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update city API key
 */
export const updateCityApiKey = async (city, apiKey) => {
  try {
    // For now, use localStorage. Replace with API call when backend is ready:
    // const response = await fetch(`${API_BASE_URL}/api/admin/cities/${city}/apikey`, {
    //   method: 'PUT',
    //   headers: getAuthHeaders(),
    //   body: JSON.stringify({ apiKey })
    // });
    // return await response.json();
    
    const config = initializeConfig();
    config.cityApiKeys[city] = apiKey;
    saveConfig(config);
    
    return { success: true, data: config.cityApiKeys };
  } catch (error) {
    console.error('Error updating city API key:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// Indicator Configuration APIs
// ============================================

/**
 * Get indicator configurations for a city
 */
export const getIndicatorConfigurations = async (city) => {
  try {
    // For now, use localStorage. Replace with API call when backend is ready:
    // const response = await fetch(`${API_BASE_URL}/api/admin/cities/${city}/indicators`, {
    //   headers: getAuthHeaders()
    // });
    // return await response.json();
    
    const config = initializeConfig();
    return {
      success: true,
      data: config.indicatorsWithData[city] || []
    };
  } catch (error) {
    console.error('Error fetching indicator configurations:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all indicator configurations
 */
export const getAllIndicatorConfigurations = async () => {
  try {
    const config = initializeConfig();
    return {
      success: true,
      data: config.indicatorsWithData
    };
  } catch (error) {
    console.error('Error fetching all indicator configurations:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Add indicator to a city
 */
export const addIndicatorToCity = async (city, indicatorName) => {
  try {
    // For now, use localStorage. Replace with API call when backend is ready:
    // const response = await fetch(`${API_BASE_URL}/api/admin/cities/${city}/indicators`, {
    //   method: 'POST',
    //   headers: getAuthHeaders(),
    //   body: JSON.stringify({ indicatorName })
    // });
    // return await response.json();
    
    const config = initializeConfig();
    if (!config.indicatorsWithData[city]) {
      config.indicatorsWithData[city] = [];
    }
    if (!config.indicatorsWithData[city].includes(indicatorName)) {
      config.indicatorsWithData[city].push(indicatorName);
      saveConfig(config);
    }
    
    return { success: true, data: config.indicatorsWithData[city] };
  } catch (error) {
    console.error('Error adding indicator to city:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Remove indicator from a city
 */
export const removeIndicatorFromCity = async (city, indicatorName) => {
  try {
    // For now, use localStorage. Replace with API call when backend is ready:
    // const response = await fetch(`${API_BASE_URL}/api/admin/cities/${city}/indicators/${encodeURIComponent(indicatorName)}`, {
    //   method: 'DELETE',
    //   headers: getAuthHeaders()
    // });
    // return await response.json();
    
    const config = initializeConfig();
    if (config.indicatorsWithData[city]) {
      config.indicatorsWithData[city] = config.indicatorsWithData[city].filter(
        ind => ind !== indicatorName
      );
      saveConfig(config);
    }
    
    return { success: true, data: config.indicatorsWithData[city] || [] };
  } catch (error) {
    console.error('Error removing indicator from city:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update multiple indicators for a city at once
 */
export const updateCityIndicators = async (city, indicators) => {
  try {
    // For now, use localStorage. Replace with API call when backend is ready:
    // const response = await fetch(`${API_BASE_URL}/api/admin/cities/${city}/indicators`, {
    //   method: 'PUT',
    //   headers: getAuthHeaders(),
    //   body: JSON.stringify({ indicators })
    // });
    // return await response.json();
    
    const config = initializeConfig();
    config.indicatorsWithData[city] = indicators;
    saveConfig(config);
    
    return { success: true, data: config.indicatorsWithData[city] };
  } catch (error) {
    console.error('Error updating city indicators:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// Bulk Operations
// ============================================

/**
 * Get complete configuration
 */
export const getCompleteConfiguration = async () => {
  try {
    const config = initializeConfig();
    return {
      success: true,
      data: config
    };
  } catch (error) {
    console.error('Error fetching complete configuration:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Reset configuration to defaults
 */
export const resetConfiguration = async () => {
  try {
    localStorage.removeItem(CONFIG_STORAGE_KEY);
    return {
      success: true,
      data: DEFAULT_CONFIG
    };
  } catch (error) {
    console.error('Error resetting configuration:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Export configuration (for backup)
 */
export const exportConfiguration = async () => {
  try {
    const config = initializeConfig();
    return {
      success: true,
      data: config
    };
  } catch (error) {
    console.error('Error exporting configuration:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Import configuration (from backup)
 */
export const importConfiguration = async (configData) => {
  try {
    // Validate config structure
    if (!configData.cityDataAvailable || !configData.indicatorsWithData || !configData.cityApiKeys) {
      throw new Error('Invalid configuration format');
    }
    
    saveConfig(configData);
    return {
      success: true,
      data: configData
    };
  } catch (error) {
    console.error('Error importing configuration:', error);
    return { success: false, error: error.message };
  }
};
